import { z } from 'zod';
import { IsoTimestamp, RunId, UserId } from './common';

/**
 * Per-application review queue (#45 — tightened 2026-06-27).
 *
 * Only emitted by the worker when an Easy-Apply form question can't be
 * answered from the Answer Bank (`no_answer_match`) or only answered
 * with low LLM confidence (`low_confidence`). Every other failure mode
 * is silent — see the plan at
 * `docs/superpowers/plans/2026-06-21-needs-review-per-application-queue.md`
 * §"Scope (tightened 2026-06-27)" for the rationale.
 *
 * One row per `(userId, linkedinJobId)`. Re-encountering the same job
 * in a later cycle bumps `occurrences` and updates `question` to the
 * most-recent unanswered prompt rather than creating a duplicate row.
 */
const REVIEW_REASON_VALUES = ['no_answer_match', 'low_confidence'] as const;
export const ReviewReason = z.enum(REVIEW_REASON_VALUES);
export type ReviewReason = z.infer<typeof ReviewReason>;

/** Forward-compat read variant — see #482. */
export const ReviewReasonReadable = z
  .enum([...REVIEW_REASON_VALUES, 'unknown'] as const)
  .catch('unknown');
export type ReviewReasonReadable = z.infer<typeof ReviewReasonReadable>;

const REVIEW_STATUS_VALUES = ['open', 'dismissed', 'answered'] as const;
export const ReviewStatus = z.enum(REVIEW_STATUS_VALUES);
export type ReviewStatus = z.infer<typeof ReviewStatus>;

/** Forward-compat read variant — see #482. */
export const ReviewStatusReadable = z
  .enum([...REVIEW_STATUS_VALUES, 'unknown'] as const)
  .catch('unknown');
export type ReviewStatusReadable = z.infer<typeof ReviewStatusReadable>;

export const ReviewItem = z
  .object({
    userId: UserId,
    /** Doubles as the review ID — one row per job per user. */
    linkedinJobId: z.string().min(1),
    runId: RunId,
    jobTitle: z.string().min(1),
    company: z.string().min(1),
    jobUrl: z.string().url(),
    /** ATS host the worker was applying through; null for LinkedIn Easy Apply. */
    atsHost: z.string().nullable(),
    /** The prompt the AI couldn't answer. PII-scrubbed at emission. */
    question: z.string().min(1).max(2000),
    /** Surrounding label / section name from the form — context for the user. */
    questionContext: z.string().nullable(),
    reason: ReviewReasonReadable,
    status: ReviewStatusReadable,
    occurrences: z.number().int().min(1),
    createdAt: IsoTimestamp,
    dismissedAt: IsoTimestamp.nullable(),
    answeredAt: IsoTimestamp.nullable(),
    /** Epoch-seconds DDB TTL. 30 days from createdAt. */
    ttl: z.number().int().positive(),
  })
  .strict();
export type ReviewItem = z.infer<typeof ReviewItem>;

/**
 * Worker → API POST body. Server stamps userId from JWT and stamps
 * status=open + occurrences + createdAt + ttl. Client cannot pre-set
 * those fields (`.strict()` rejects unknowns).
 *
 * `reason` is overridden to the strict enum so the write path continues
 * to reject unknown values even though the read shape now folds them
 * into `'unknown'` for forward-compat (#482).
 */
export const CreateReviewItemRequest = ReviewItem.omit({
  userId: true,
  status: true,
  occurrences: true,
  createdAt: true,
  dismissedAt: true,
  answeredAt: true,
  ttl: true,
})
  .extend({ reason: ReviewReason })
  .strict();
export type CreateReviewItemRequest = z.infer<typeof CreateReviewItemRequest>;

/**
 * Mobile → API PATCH body. Only `dismissed` and `answered` are
 * client-driven transitions. `open` is the worker-set initial state.
 */
export const PatchReviewItemRequest = z
  .object({
    status: z.enum(['dismissed', 'answered']),
  })
  .strict();
export type PatchReviewItemRequest = z.infer<typeof PatchReviewItemRequest>;

/**
 * `GET /v1/needs-review` response.
 */
export const ListReviewItemsResponse = z.object({
  items: z.array(ReviewItem),
});
export type ListReviewItemsResponse = z.infer<typeof ListReviewItemsResponse>;
